import { type FC } from 'react';
import {
  Button,
  Flex,
  Heading,
  Icon,
  LoadingDots,
  Text,
} from '@highereducation/hen-ui';
import { SubmissionStatus } from '../ResultsPage/types';
import type { SubmissionStatusProps } from './types';

const SubmissionStatusPanel: FC<SubmissionStatusProps> = ({
  status,
  schoolName,
  onRetry,
  onCloseExperience,
}) => {
  if (!status) return null;

  return (
    <Flex
      direction='column'
      justify='center'
      align='center'
      height='full'
      width='full'
      className='min-h-status-panel px-3 text-center'
    >
      <Flex
        key={status}
        direction='column'
        justify='center'
        align='center'
        gap='md'
        className='animate-fade-in-0 motion-reduce:animate-none'
      >
        {status === SubmissionStatus.SUBMITTING && (
          <>
            <LoadingDots
              label={`Requesting information from ${schoolName}...`}
              color='primary'
            />
            <Heading variant='h4'>
              Requesting information from {schoolName}...
            </Heading>
          </>
        )}
        {status === SubmissionStatus.SUBMITTED && (
          <>
            <Icon icon='circle-check' size='4xl' color='primary' aria-hidden />
            <Text variant='b1'>
              Successfully requested information from{' '}
              <strong>{schoolName}!</strong>
            </Text>
          </>
        )}
        {status === SubmissionStatus.ERROR && (
          <>
            <Icon
              icon='circle-alert'
              size='4xl'
              color='secondary'
              aria-hidden
            />
            <Text variant='b1'>
              Unable to request information from <strong>{schoolName}</strong>
            </Text>
            <Text variant='b3' color='subtext'>
              Something went wrong. Please try again in a moment.
            </Text>
            {onRetry && (
              <Button variant='primaryFill' onClick={onRetry}>
                Try again
              </Button>
            )}
          </>
        )}
        {status === SubmissionStatus.UNQUALIFIED && (
          <>
            <Icon icon='circle-alert' size='4xl' color='warning' aria-hidden />
            <Heading variant='h4'>
              This program isn&apos;t a fit right now
            </Heading>
            <Text variant='b3' color='subtext' className='max-w-sm'>
              You don&apos;t meet the qualification requirements for this
              program. You can still explore other programs that may be a better
              fit.
            </Text>
            <Button variant='primaryFill' onClick={onCloseExperience}>
              Browse other programs
            </Button>
          </>
        )}
      </Flex>
    </Flex>
  );
};

export default SubmissionStatusPanel;
